import api from '@/apis'
import { createLatestOnly } from '@/utils/latestOnly'

// Re-dispatched after every write on this screen, so two can be in flight with the SAME
// parameters and DIFFERENT data — which is why 'takes no parameters' is not an exemption.
const profiles_request = createLatestOnly()
import { request } from '@/utils/storeRequest'

// Booking import profiles decide, per uploaded row, which reserved queue an online
// booking lands in (matched on the row's CS Center) and how far around the booked
// timeslot a customer may still check in.
export default {
  state: {
    loading: true,
    headers: [
      { text: '#', value: 'index', sortable: false, width: 60 },
      { text: 'Name', value: 'name', sortable: false },
      { text: 'Target Queue', value: 'service_type', sortable: false },
      { text: 'Fallback (Walk-in) Queue', value: 'fallback_service_type', sortable: false },
      { text: 'CS Center', value: 'source_cs_center', sortable: false, width: 100 },
      { text: 'Default', value: 'is_default', sortable: false, width: 90 },
      { text: 'Threshold (Mins)', value: 'threshold_minutes', sortable: false, width: 130 },
      { text: 'Active', value: 'active', sortable: false, width: 90 },
      { text: 'Action', value: 'action', sortable: false, width: 110 }
    ],
    items: []
  },
  getters: {
    // "If this profile stopped being the default, would another one still be?"
    // Unmatched CS Center values are routed to the default profile, so the last
    // default must never be edited or deleted away.
    // Deleting a queue that a profile routes to (or falls back to) would break booking
    // import; Queue Setting uses this to refuse before calling the backend.
    profilesUsingServiceType: state => service_type_id => state.items.filter(profile => profile.service_type_id === service_type_id || profile.fallback_service_type_id === service_type_id),
    hasOtherDefaultProfile: state => booking_import_profile_id => state.items.some(profile => profile.booking_import_profile_id !== booking_import_profile_id && Number(profile.is_default) === 1)
  },
  mutations: {
    SET_BOOKING_PROFILE_LOADING(state, payload) {
      state.loading = payload
    },
    SET_BOOKING_PROFILE_ITEMS(state, payload) {
      // Backends that predate the single-threshold API only expose the raw window
      // columns, where valid_before_minutes is the threshold T (valid_after_minutes is
      // T + the 30-minute slot). Normalise once here so every consumer just reads
      // threshold_minutes.
      state.items = (Array.isArray(payload) ? payload : []).map(profile => ({
        ...profile,
        threshold_minutes: profile.threshold_minutes == null ? profile.valid_before_minutes : profile.threshold_minutes
      }))
    }
  },
  actions: {
    async fetchBookingProfiles({ commit }) {
      const isLatest = profiles_request()
      try {
        commit('SET_BOOKING_PROFILE_LOADING', true)
        // include_inactive keeps deactivated profiles visible so they can be re-enabled;
        // the plain GET used by the import dialog still returns active profiles only.
        const response = await api.getBookingImportProfiles({ include_inactive: 1 })
        // A newer request of this kind owns the view now — see utils/latestOnly.js.
        if (!isLatest()) return
        if (response.data.success) {
          commit('SET_BOOKING_PROFILE_ITEMS', response.data.data)
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '3000' })
        }
      } catch (error) {
        if (!isLatest()) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '3000' })
      } finally {
        // Guarded like every other write in this action: a superseded request clearing the
        // spinner would make the table read "no data" while a newer request is still loading.
        if (isLatest()) commit('SET_BOOKING_PROFILE_LOADING', false)
      }
    },
    // Create or update depending on whether the payload carries an id.
    async saveBookingProfile({ commit, dispatch }, payload) {
      const saved = await request(commit, {
        call: () => (payload.params.booking_import_profile_id ? api.updateBookingImportProfile(payload) : api.createBookingImportProfile(payload)),
        loading: 'Saving...',
        success: 'Success'
      })
      if (saved) dispatch('fetchBookingProfiles')
      return saved
    },
    async deleteBookingProfile({ commit, dispatch }, payload) {
      const deleted = await request(commit, {
        call: () => api.deleteBookingImportProfile(payload),
        loading: 'Deleting...',
        success: 'Success'
      })
      if (deleted) dispatch('fetchBookingProfiles')
      return deleted
    }
  }
}
